/**
 * <judgeme-rating> — moves the Judge.me preview badge next to the price.
 *
 *   <judgeme-rating data-target=".product__price-wrapper"></judgeme-rating>
 *
 * Judge.me injects `.jdgm-widget.jdgm-preview-badge` into its own placeholder
 * somewhere inside the product, asynchronously and sometimes more than once
 * (variant change, late script). This element:
 *   1. finds its product root — the closest [data-component="product"]
 *   2. waits for the badge with one MutationObserver, throttled to a frame
 *   3. moves the badge into the target and drops the wrappers it leaves empty
 *   4. intercepts clicks on the badge, in the capture phase, and forwards them
 *      to Judge.me's own "write a review" button
 *
 * The observer is cheap (one per element, a single boolean check per batch),
 * disconnects once the badge has settled, and is skipped entirely if the badge
 * is already in place when the element connects.
 */
(() => {
  if (customElements.get('judgeme-rating')) return;

  const BADGE = '.jdgm-widget.jdgm-preview-badge';
  const WRITE_REVIEW =
    '.jdgm-review-widget .jm-action-buttons [data-testid="write-review-button"]';
  // Stop watching this long after the badge lands; covers a late re-render
  // without leaving an observer running for the life of the page.
  const SETTLE_MS = 5000;
  // Give up if Judge.me never loads, rather than observing forever.
  const TIMEOUT_MS = 20000;

  class JudgemeRating extends HTMLElement {
    connectedCallback() {
      this.product = this.closest('[data-component="product"]');
      if (!this.product) return;

      this.target = this.product.querySelector(
        this.dataset.target || '.product__price-wrapper'
      );
      if (!this.target) return;

      this.bindClick();

      // Already there (re-render of a section that kept the moved badge)
      if (this.move()) {
        this.settle();
        return;
      }

      this.observe();
      this.timeout = setTimeout(() => this.stop(), TIMEOUT_MS);
    }

    disconnectedCallback() {
      this.stop();
      if (this.target) {
        this.target.removeEventListener('click', this.handleClick, true);
        this.bound = false;
      }
    }

    stop() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      clearTimeout(this.timeout);
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.frame);
    }

    observe() {
      // One batched check per frame: mutation batches during a widget render
      // can be large, and the check itself is a single querySelector.
      this.observer = new MutationObserver(this.handleMutations);
      this.observer.observe(this.product, { childList: true, subtree: true });
    }

    /** Arrow functions: `this` stays the element, and the same reference can
        be removed again on disconnect. */
    handleMutations = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(this.handleFrame);
    };

    handleFrame = () => {
      this.frame = 0;
      if (this.move()) this.settle();
    };

    handleClick = (event) => {
      // Only the badge itself: the price and any links beside it keep
      // working normally.
      if (!event.target.closest(BADGE)) return;

      // Capture phase, so Judge.me's own handler (it scrolls to the widget)
      // never runs.
      event.preventDefault();
      event.stopImmediatePropagation();

      const button = document.querySelector(WRITE_REVIEW);
      if (button) button.click();
    };

    /** Keep watching briefly in case Judge.me re-renders, then stop. */
    settle() {
      if (!this.observer) return;
      clearTimeout(this.timeout);
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.stop(), SETTLE_MS);
    }

    /** @returns {boolean} true once the badge sits in the target. */
    move() {
      const badge = this.product.querySelector(BADGE);
      if (!badge) return false;
      if (badge.parentElement === this.target) return true;

      // Remember the wrappers Judge.me put around the badge so the empty ones
      // can go with it; stop at the target or the product root.
      const orphans = [];
      let parent = badge.parentElement;
      while (parent && parent !== this.target && parent !== this.product) {
        orphans.push(parent);
        parent = parent.parentElement;
      }

      this.target.appendChild(badge);
      this.setAttribute('data-moved', '');

      orphans.forEach((node) => {
        // Only drop what is genuinely left over — never a wrapper that still
        // holds markup of its own.
        if (node.isConnected && !node.firstElementChild && !node.textContent.trim()) {
          node.remove();
        }
      });

      return true;
    }

    bindClick() {
      if (this.bound) return;
      this.bound = true;
      this.target.addEventListener('click', this.handleClick, true);
    }
  }

  customElements.define('judgeme-rating', JudgemeRating);
})();
